import React, { useCallback, useEffect, useState } from 'react'
import type { Key, PressEvent } from 'react-aria-components'
import { Button, Menu, MenuItem, MenuTrigger } from 'react-aria-components'
import { flushSync } from 'react-dom'
import { useTranslation } from 'react-i18next'

import { CommandPalette } from '#components/CommandPalette'
import { Icon } from '#components/Icon'
import { Popover } from '#components/Popover'
import { WijMobielLogo } from '#components/WijMobielLogo'
import { useAuth } from '#hooks/useAuth'
import { useLanguageToggle } from '#hooks/useLanguageToggle'
import { useSidebar } from '#hooks/useSidebar'
import { useTheme } from '#hooks/useTheme'
import { ROLE_ADMIN } from '#interfaces/auth'
import { LOCALE_LABELS, SUPPORTED_LOCALES } from '#lib/i18n'

import { CompanySwitcher } from './components/CompanySwitcher'
import { FLAG_ICONS } from './flags'
import styles from './styles.module.scss'

export const GlobalControlBar = (): React.JSX.Element => {
  const { t } = useTranslation()
  const { isAuthenticated, hasMinimumRole } = useAuth()
  // The backend serves /search to admins only.
  const canSearch = isAuthenticated && hasMinimumRole(ROLE_ADMIN)
  const { currentLocale, changeLanguage } = useLanguageToggle()
  const { isCollapsed, isMobile, toggleSidebar } = useSidebar()
  const { resolvedTheme, toggleTheme } = useTheme()
  const [isSearchOpen, setIsSearchOpen] = useState<boolean>(false)
  const [paletteQuery, setPaletteQuery] = useState<string>('')

  const openPalette = useCallback((query: string): void => {
    setPaletteQuery(query)
    setIsSearchOpen(true)
  }, [])

  useEffect((): (() => void) => {
    if (!canSearch) return (): void => undefined

    const handleKeyDown = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        openPalette('')
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return (): void => document.removeEventListener('keydown', handleKeyDown)
  }, [canSearch, openPalette])

  const handleThemeToggle = useCallback(
    (e: PressEvent): void => {
      const skipAnimation =
        !document.startViewTransition || window.matchMedia('(prefers-reduced-motion: reduce)').matches

      if (skipAnimation) {
        toggleTheme()
        return
      }

      if (!(e.target instanceof HTMLElement)) return

      const rect = e.target.getBoundingClientRect()
      const x = rect.left + rect.width / 2
      const y = rect.top + rect.height / 2
      const maxRadius = Math.hypot(Math.max(x, window.innerWidth - x), Math.max(y, window.innerHeight - y))

      const transition = document.startViewTransition(() => {
        // flushSync is required so startViewTransition captures the correct "after" DOM snapshot
        flushSync(toggleTheme) // eslint-disable-line @eslint-react/dom/no-flush-sync
      })

      void transition.ready.then((): void => {
        document.documentElement.animate(
          { clipPath: [`circle(0px at ${x}px ${y}px)`, `circle(${maxRadius}px at ${x}px ${y}px)`] },
          { duration: 400, easing: 'ease-out', pseudoElement: '::view-transition-new(root)' },
        )
      })
    },
    [toggleTheme],
  )

  const handleLanguageSelect = useCallback(
    (key: Key): void => {
      const locale = String(key)
      const matched = SUPPORTED_LOCALES.find((l) => l === locale)
      if (matched) changeLanguage(matched)
    },
    [changeLanguage],
  )

  const FlagIcon = FLAG_ICONS[currentLocale]
  const showCompanySwitcher = isAuthenticated

  return (
    <div className={styles.controlBar} data-testid="global-control-bar">
      <div className={styles.logoArea}>
        {isMobile && (
          <Button
            className={styles.iconButton}
            onPress={toggleSidebar}
            aria-label={isCollapsed ? t('Open menu') : t('Close menu')}
            data-testid="hamburger-button"
          >
            <Icon name="menu" width="20" height="20" />
          </Button>
        )}
        <WijMobielLogo size="inherit" className={styles.logo} />
      </div>

      {canSearch ? (
        <Button
          className={styles.topBarSearch}
          onPress={(): void => openPalette('')}
          aria-label={t('Search')}
          data-testid="topbar-search"
        >
          <Icon name="search" width="14" height="14" className={styles.topBarSearchIcon} />
          <span className={styles.topBarSearchPlaceholder}>{t('Search users, companies, cards...')}</span>
          <kbd className={styles.searchKbd}>⌘K</kbd>
        </Button>
      ) : (
        <span className={styles.spacer} />
      )}

      <div className={styles.controls}>
        {showCompanySwitcher && <CompanySwitcher />}

        <Button
          className={styles.iconButton}
          onPress={handleThemeToggle}
          data-testid="theme-toggle"
          aria-label={resolvedTheme === 'light' ? t('Switch to dark mode') : t('Switch to light mode')}
        >
          <Icon name={resolvedTheme === 'light' ? 'moon' : 'sun'} width="18" height="18" />
        </Button>

        <MenuTrigger>
          <Button className={styles.languageButton} aria-label={t('Select language')} data-testid="language-toggle">
            <FlagIcon />
          </Button>
          <Popover isDialog={false} placement="bottom end" width="sm" data-testid="language-menu-popover">
            <Menu
              className={styles.languageMenu}
              onAction={handleLanguageSelect}
              selectedKeys={[currentLocale]}
              selectionMode="single"
              data-testid="language-menu"
            >
              {SUPPORTED_LOCALES.map((locale) => {
                const LocaleFlag = FLAG_ICONS[locale]
                return (
                  <MenuItem key={locale} id={locale} className={styles.languageMenuItem}>
                    <LocaleFlag />
                    <span>{LOCALE_LABELS[locale]}</span>
                  </MenuItem>
                )
              })}
            </Menu>
          </Popover>
        </MenuTrigger>
      </div>

      {canSearch && (
        <CommandPalette
          isOpen={isSearchOpen}
          onClose={(): void => {
            setIsSearchOpen(false)
            setPaletteQuery('')
          }}
          initialQuery={paletteQuery}
        />
      )}
    </div>
  )
}
